import { Component, OnInit, Inject } from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Rule } from "@aptsi-types";
import { RuleService } from "../../rules.service";
import { MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { RulesetService } from "src/app/features/ada-access/ruleset/ruleset.service";
import { ErrorDialogComponent } from "src/app/features/ada-access/shared/dialogs/error-dialog.component";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { SuccessDialogComponent } from "../../../shared/dialogs/success-dialog/success-dialog.component";
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { EnvironmentService } from "src/app/core/service/environment.service";
@Component({
  selector: "app-rules-create-or-update",
  templateUrl: "./rules-create-or-update.component.html",
  styleUrls: ["./rules-create-or-update.component.scss"],
})
export class RulesCreateOrUpdateComponent implements OnInit {
  // ─────────────────────────────
  // User Context
  // ─────────────────────────────
  isCreate = false;
  existingRuleId: string = null;

  // ─────────────────────────────
  // Data
  // ─────────────────────────────
  rule: Rule = null;
  variables: string[] = [];
  filteredVariables: string[] = [];

  // ─────────────────────────────
  // Formula & Search
  // ─────────────────────────────
  searchTerm: string = "";
  formulaTokens: string[] = [];
  currentFormula: string = "";
  customString: string = "";
  isNumber: boolean = false;
  organizationId: string;

  // ─────────────────────────────
  // UI Config
  // ─────────────────────────────
  readonly typeOptions = ["predicate", "derivative", "None"];
  form: FormGroup;

  // ─────────────────────────────
  // Lifecycle
  // ─────────────────────────────
  constructor(
    private route: ActivatedRoute,
    private ruleService: RuleService,
    private dialog: MatDialog,
    private router: Router,
    private ruleSetService: RulesetService,
    private keycloakStateService: KeycloakStateService,
    public environmentService: EnvironmentService 
  ) { }

  async ngOnInit(): Promise<void> {
    this.organizationId = this.keycloakStateService.getOrganizationId(); // <-- Retrieve here
    console.log('Organization ID in component:', this.organizationId);
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.existingRuleId = this.route.snapshot.params.ruleId;

    if (!this.isCreate) {
      this.fetchRules();
    }

    this.form = this.isCreate
      ? new FormGroup({
        ruleId: new FormControl(null),
        name: new FormControl(null, Validators.required),
        type: new FormControl("None", [
          Validators.required,
          this.typeValidator,
        ]),
        stringRepresentation: new FormControl(null, Validators.required),
      })
      : new FormGroup({
        ruleId: new FormControl(this.existingRuleId, Validators.required),
        name: new FormControl(null, Validators.required),
        type: new FormControl("None", [
          Validators.required,
          this.typeValidator,
        ]),
        stringRepresentation: new FormControl(null, Validators.required),
      });

    this.fetchRiskAttributes();
  }

  // ─────────────────────────────
  // Formula Handling
  // ─────────────────────────────
  updateFormula() {
    this.currentFormula = this.formulaTokens.join(" ");
    this.form.get("stringRepresentation").setValue(this.currentFormula.trim());
  }

  selectVariable(variable: string) {
    this.formulaTokens.push(variable);
    this.updateFormula();
  }

  selectOperator(operator: string) {
    this.formulaTokens.push(operator);
    this.updateFormula();
  }

  selectNumber(num: number) {
    const digit = num.toString();
    const lastToken =
      this.formulaTokens.length > 0
        ? this.formulaTokens[this.formulaTokens.length - 1]
        : undefined;

    if (lastToken && /^\d+$/.test(lastToken)) {
      this.formulaTokens[this.formulaTokens.length - 1] += digit;
    } else {
      this.formulaTokens.push(digit);
    }
    this.updateFormula();
  }

  addStringToFormula(customString: string) {
    if (this.customString.trim()) {
      this.formulaTokens.push(`"${this.customString.trim()}"`);
      this.updateFormula();
    }
    this.customString = "";
  }

  removeLastEntry() {
    this.formulaTokens.pop();
    this.updateFormula();
  }

  clearFormula() {
    this.formulaTokens = [];
    this.updateFormula();
  }

  // ─────────────────────────────
  // Search & Filtering
  // ─────────────────────────────
  onSearchChange() {
    this.filteredVariables = this.variables.filter((variable) =>
      variable.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  // ─────────────────────────────
  // Data Fetching
  // ─────────────────────────────
  async fetchRules() {
    try {
      const response = await this.ruleService
        .getRule(this.existingRuleId)
        .toPromise();
      this.rule = response["Resource retrieved successfully"]?.[0];

      if (this.rule) {
        this.form.patchValue({
          name: this.rule.ruleName,
          type: this.rule.ruleType,
          stringRepresentation: this.rule.representation,
        });

        this.formulaTokens = this.rule.representation.split(" ");
        this.updateFormula();
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  }

  async fetchRiskAttributes() {
    try {
      const response = await this.ruleService.getRiskAttributes().toPromise();

      if (Array.isArray(response.riskAttributes)) {
        this.variables = response.riskAttributes;
        this.filteredVariables = [...this.variables];
      } else {
        console.error("Expected riskAttributes array but received:", response);
        this.variables = [];
        this.filteredVariables = [];
      }
    } catch (error) {
      console.error("Error fetching risk attributes:", error);
      this.variables = [];
      this.filteredVariables = [];
    }
  }

  // ─────────────────────────────
  // Validation
  // ─────────────────────────────
  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    return control.value === "None" ? { noneSelected: true } : null;
  }

  // ─────────────────────────────
  // Submit Logic
  // ─────────────────────────────
  async submit() {
    try {
      this.form.get("ruleId").setErrors(null);

      if (this.isCreate) {
        await this.ruleService
          .createRule(
            this.form.value.name,
            this.form.value.type,
            this.form.value.stringRepresentation,
            this.organizationId
          )
          .toPromise();
      } else {
        await this.ruleService
          .updateRule(
            this.existingRuleId,
            this.form.value.name,
            this.form.value.type,
            this.form.value.stringRepresentation,
            this.organizationId
          )
          .toPromise();
      }

      this.openSuccessDialog(this.isCreate);
    } catch (error) {
      this.openErrorDialog(error.message || "An unexpected error occurred.");
    }
  }

  // ─────────────────────────────
  // Navigation
  // ─────────────────────────────
  cancel() {
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/rules/search",
    ]);
  }

  goToRuleset() {
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/ruleset/create",
    ]);
  }

  // ─────────────────────────────
  // Dialogs
  // ─────────────────────────────
  openSuccessDialog(isCreate: boolean): void {
    this.dialog
      .open(SuccessDialogComponent, {
        width: "250px",
        data: { isCreate, title: "Rule" },
      })
      .afterClosed()
      .subscribe(() => {
        this.router.navigate([
          "ada-access/auth-tools/policy-authoring/rules/search",
        ]);
      });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage },
    });
  }

  // ─────────────────────────────
  // Helpers
  // ─────────────────────────────
  async checkRuleIdExists(ruleId: string): Promise<boolean> {
    try {
      const response = await this.ruleService.getRule(ruleId).toPromise();
      return (
        Array.isArray(response["Resource retrieved successfully."]) &&
        response["Resource retrieved successfully."].length > 0
      );
    } catch (error) {
      console.error("Error checking ruleId existence:", error);
      return false;
    }
  }

  async checkRulesetIdExists(rulesetId: string): Promise<boolean> {
    try {
      const response = await this.ruleSetService
        .getRuleset(rulesetId)
        .toPromise();
      return (
        Array.isArray(response["Resource retrieved successfully."]) &&
        response["Resource retrieved successfully."].length > 0
      );
    } catch (error) {
      console.error("Error checking rulesetId existence:", error);
      return false;
    }
  }
}
